import fs from "node:fs";
import path from "node:path";
import type { CookieMap, Response } from "@iflyrpa/share";
import { downloadImage, getFilenameFromUrl, success } from "@iflyrpa/share";
import axios from "axios";

import { copyHtmlToClipboard, PopupListener, parseUrlQueryString, TimeFormatter } from "../../utils";
import type { PublishAction } from "./index";
import { scanRetryMaxCount } from "./utils";

// 等待二维码验证结果最长时间
const waitQrcodeResultMaxTime = scanRetryMaxCount * 2 * 1000;

export const rpaAction: PublishAction = async (task, params) => {
    const tmpCachePath = task.getTmpPath();

    const commonCookies = {
        path: "/",
        secure: true,
        domain: "mp.weixin.qq.com",
        url: "https://mp.weixin.qq.com",
        httpOnly: true,
    };

    const urlParams = {
        action: "edit",
        type: 77,
        token: params.token,
        lang: "zh_CN",
        timestamp: Date.now(),
        appmsgid: params.appMsgId,
    };

    if (params.settingInfo?.wxRewardTarget) {
        const urlRewardParams = {
            action: "inviteauthor",
            token: params.token,
            lang: "zh_CN",
        };
        const wxRewardPage = await task.createPage({
            show: task.debug,
            url:
                params.url ||
                `https://mp.weixin.qq.com/cgi-bin/safecenterstatus?${Object.entries(urlRewardParams)
                    .map((it) => it.join("="))
                    .join("&")}`,
            cookies: params.cookies?.map((it) => ({ ...it, ...commonCookies })) || [],
        });

        const reward_page = wxRewardPage.locator("#app");
        const isAddRewarder = reward_page
            .locator(".author_card_bd.weui-desktop-vm_primary")
            .filter({ hasText: params.settingInfo.wxRewardTarget.author })
            .count();
        if ((await isAddRewarder) === 0) {
            await reward_page.locator(".weui-desktop-panel__hd-right").click();
            try {
                try {
                    await wxRewardPage.waitForSelector(".invite-qrcode", { state: "visible", timeout: 20000 });
                    const qrcodeSrc = await reward_page.locator(".invite-qrcode").getAttribute("src");

                    if (qrcodeSrc) {
                        const rewardBuffer = Buffer.from(qrcodeSrc.replace(/^data:image\/\w+;base64,/, ""), "base64");
                        const rewardFilePath = path.join(tmpCachePath, "weixin_reward.jpg");
                        fs.writeFileSync(rewardFilePath, new Uint8Array(rewardBuffer));

                        params.safeQrcodeCallback?.(rewardFilePath);
                    }

                    // await wxRewardPage.waitForSelector('[data-transfer="true"]:not([weui="true"])', { state: 'detached', timeout: 30000 });
                } catch (error) {
                    throw new Error("二维码已过期，请重试绑定。");
                }

                for (let i = 0; i < scanRetryMaxCount; i++) {
                    await Promise.all([wxRewardPage.reload(), wxRewardPage.waitForLoadState("domcontentloaded")]);

                    try {
                        await wxRewardPage.waitForSelector(
                            `.author_card_bd.weui-desktop-vm_primary:has-text("${params.settingInfo.wxRewardTarget?.author}")`,
                            { timeout: 1000 },
                        );
                        break;
                    } catch (error) {}

                    if (i === scanRetryMaxCount - 1) throw new Error("未确认绑定账户，请在手机端确认绑定并重试");
                }
            } catch (error) {
                let errorMessage = "发生未知错误";
                if (error instanceof Error) {
                    errorMessage = error.message;
                } else if (typeof error === "string") {
                    errorMessage = error;
                }
                await wxRewardPage.close();
                return { code: 414, message: errorMessage, data: "" };
            }
        }

        if (params.settingInfo.wxRewardText) {
            await wxRewardPage.locator('[href="/merchant/reward?action=getautoreply"]').click();
            await wxRewardPage.waitForLoadState("networkidle");

            const isAddWord = reward_page
                .locator(".auto-reply__list.edit .auto-reply__hd .auto-reply__text")
                .filter({ hasText: new RegExp(`^${params.settingInfo.wxRewardText}$`) })
                .count();

            if ((await isAddWord) === 0) {
                await reward_page.locator(".emotion_editor .edit_area").fill(params.settingInfo.wxRewardText);
                await reward_page.locator(".emotion_editor .edit_area").click();
                await reward_page.locator(".editor_toolbar .weui-desktop-btn.weui-desktop-btn_primary").click();
            }
        }
        await wxRewardPage.close();
    }

    const page = await task.createPage({
        show: task.debug,
        url:
            params.url ||
            `https://mp.weixin.qq.com/cgi-bin/appmsg?${Object.entries(urlParams)
                .map((it) => it.join("="))
                .join("&")}`,
        cookies: params.cookies?.map((it) => ({ ...it, ...commonCookies })) || [],
    });

    // 0. 校验登录
    try {
        await page.waitForSelector(".weui-desktop-head", { state: "visible" });
    } catch (error) {
        return { code: 414, message: "登录失效", data: page.url() };
    }

    // 1. 等待编辑器加载出来
    try {
        await page.waitForSelector("#editor_pannel", { state: "visible", timeout: 60000 });
    } catch (error) {
        return { code: 414, message: "编辑器加载超时，请稍后重试", data: page.url() };
    }

    //新功能更新监听
    PopupListener(
        page,
        '[v-transfer-dom="#vue_app"] .weui-desktop-dialog',
        "更新",
        '[v-transfer-dom="#vue_app"] .weui-desktop-icon-btn.weui-desktop-dialog__close-btn',
        {
            checkInterval: 1000,
            stopAfterClick: false,
        },
    );
    //小店功能开通监听
    PopupListener(
        page,
        '[extclass="weui-desktop-dialog_simple"] .weui-desktop-dialog',
        "小店",
        '[extclass="weui-desktop-dialog_simple"] .weui-desktop-icon-btn.weui-desktop-dialog__close-btn',
        {
            checkInterval: 1000,
            stopAfterClick: true,
        },
    );

    // 2. 填写标题
    const titleInstance = page.locator("#js_title_main textarea#title");
    await titleInstance.click();
    await titleInstance.fill(params.title);

    // 3. 填写作者
    if (params.settingInfo.wxAuthor) {
        const titleInstance = page.locator("#js_author_area input#author");
        await titleInstance.click();
        await titleInstance.fill(params.settingInfo.wxAuthor);
    }

    // 4. 填写正文
    await copyHtmlToClipboard(page, params.content);
    // 定位目标元素并聚焦
    await page.click(".js_editor_area .ProseMirror");
    // 模拟Ctrl+V粘贴
    await page.keyboard.press("Control+V");

    // 将设置区域滚动到可见区域
    await page.locator("#article_setting_area").evaluate((node) => node.scrollIntoView());

    // 5. 填写摘要
    if (params.settingInfo.wxAbstract) {
        const titleInstance = page.locator("#article_setting_area #js_description_area textarea");
        await titleInstance.click();
        await titleInstance.fill(params.settingInfo.wxAbstract);
    }

    // 6. 设置封面
    if (params.settingInfo.wxCover) {
        let fileName = getFilenameFromUrl(params.settingInfo.wxCover);
        const ext = path.extname(fileName);
        fileName = ext ? fileName : `${fileName}.png`;
        const cover = await downloadImage(params.settingInfo.wxCover, path.join(tmpCachePath, fileName));

        // 从图片库选择
        await page.locator("#article_setting_area #js_cover_area .select-cover__btn").hover();
        await page
            .locator("#article_setting_area #js_cover_area .pop-opr__group-select-cover .pop-opr__list .pop-opr__item")
            .filter({ hasText: "从图片库选择" })
            .click();

        // 在弹窗内选择「我的图片」- 「上传文件」
        // 等待弹窗出现
        await page.waitForSelector("#vue_app .weui-desktop-dialog", { state: "visible" });
        const dialog = page.locator("#vue_app .weui-desktop-dialog");
        // 点击「我的图片」
        await dialog.locator(".weui-desktop-menu .weui-desktop-menu__item").filter({ hasText: "我的图片" }).click();
        await page.waitForTimeout(300);
        // 点击「上传文件」
        const fileChooserPromise = page.waitForEvent("filechooser");
        await dialog.locator(".weui-desktop-img-picker__list__area .js_upload_btn_container").click();
        const fileChooser = await fileChooserPromise;
        await fileChooser.setFiles([cover]);

        // 等待图片上传完成
        try {
            await page.waitForSelector(".weui-desktop-img-picker__list .weui-desktop-img-picker__item.selected", {
                state: "visible",
                timeout: 60000,
            });
        } catch (error) {
            return { code: 414, message: "封面图片上传超时，请检查网络后重试", data: "" };
        }
        await page.waitForSelector(".weui-desktop-upload__file__progress", { state: "detached", timeout: 60000 });

        const pic_msg = page.locator(
            ".weui-desktop-img-picker__list .weui-desktop-img-picker__item.selected .weui-desktop-upload__file__msg",
        );
        if ((await pic_msg.count()) > 0)
            return {
                code: 414,
                message: `图片上传错误：${(await pic_msg.textContent()) || "未知错误，请重试！"}`,
                data: "",
            };

        // 选择下一步
        await dialog
            .locator(".weui-desktop-dialog__ft .weui-desktop-btn:not(.weui-desktop-btn_disabled)")
            .filter({ hasText: "下一步" })
            .click();
        // 等待第二步的确认按钮出来
        await dialog
            .locator(".weui-desktop-dialog__ft .weui-desktop-btn")
            .filter({ hasText: "确认" })
            .waitFor({ state: "visible" });
        await dialog.locator(".weui-desktop-dialog__ft .weui-desktop-btn").filter({ hasText: "确认" }).click();
    }

    // 7. 原创与赞赏设置
    if (params.settingInfo.wxCopyright) {
        await page.locator("#js_original").click();

        const ori_dialog = page.locator(".claim__original-dialog.original_dialog");
        const ori_dialog_bd = ori_dialog.locator(".weui-desktop-dialog__bd");
        const ori_dialog_ft = ori_dialog.locator(".weui-desktop-dialog__ft");

        await ori_dialog_bd.locator(".weui-desktop-form__check-content").filter({ hasText: "文字原创" }).click();

        if (params.settingInfo?.wxWhitelist) {
            for (const _target of params.settingInfo.wxWhitelist) {
                await ori_dialog_bd.locator("#js_article_whitelist_search").fill(_target);
                await ori_dialog_bd.locator(".js_search").click();
                const search_res = ori_dialog_bd.locator("#js_article_whitelist_search_result > div");

                await page.waitForTimeout(1000);

                if ((await search_res.getAttribute("class")) !== "search-result-title search-result-empty") {
                    const search_res_name =
                        (await search_res.locator(".search-result-name-left").textContent()) || undefined;
                    await search_res.click();
                    const whrite_list = ori_dialog_bd.locator("#js_article_whitelist_added tbody");
                    const white_td_isadd = await whrite_list
                        .locator(".table-td-name")
                        .filter({ hasText: search_res_name })
                        .count();

                    if (!white_td_isadd) {
                        return { code: 414, message: "白名单添加失败，请重试！", data: "" };
                    }
                } else {
                    return { code: 414, message: `白名单用户：${_target} 未搜索到，请确定输入是否正确！`, data: "" };
                }
            }
        }

        if (params.settingInfo?.wxQuickReprint) {
            const btn_isChecked =
                (await ori_dialog_bd.locator('//span[@class="frm_tips"]').filter({ hasText: "已开启" }).count()) === 1;
            if (!btn_isChecked)
                await ori_dialog_bd.locator(".weui-desktop-switch_loading .weui-desktop-switch__box").click();
        }

        const checkbox = ori_dialog_ft.locator(".weui-desktop-icon-checkbox");
        if (!(await checkbox.isChecked())) await checkbox.check();
        await ori_dialog_ft.locator(".weui-desktop-btn.weui-desktop-btn_primary").click();

        if (params.settingInfo?.wxRewardTarget) {
            await page.locator("#js_reward_setting_area").click();

            const reward_setting = page.locator(".reward-setting-dialog");
            const reward_setting_bd = reward_setting.locator(".weui-desktop-dialog__bd");
            const reward_setting_ft = reward_setting.locator(".weui-desktop-dialog__ft");

            //遍历绑定信息
            for (const _type in params.settingInfo.wxRewardTarget) {
                if (_type === "author") {
                    const userName = params.settingInfo.wxRewardTarget[_type];

                    await reward_setting_bd.locator(".weui-desktop-form__check-label", { hasText: "赞赏作者" }).click();
                    await reward_setting_bd.locator(".weui-desktop-form__input").fill(userName);

                    if (params.settingInfo.wxRewardText) {
                        const reward_setting_replay = page.locator(".reward-reply-setting");
                        const switchAuto = reward_setting_replay.locator(".weui-desktop-switch__box");

                        const maxAttempts = 5;
                        const delayMs = 500;
                        let nowCheckTime = 0;

                        for (let attempt = 0; attempt < maxAttempts; attempt++) {
                            const isDisabled = await switchAuto.isDisabled();
                            if (!isDisabled) {
                                (await switchAuto.isChecked()) ? nowCheckTime++ : null;
                                if (nowCheckTime === 2) break;
                            }

                            if (attempt === maxAttempts - 1)
                                throw new Error("Checkbox did not become enabled within the timeout.");
                            await new Promise((resolve) => setTimeout(resolve, delayMs));
                        }

                        const isOneReplay =
                            (await reward_setting_replay
                                .locator(".reward-reply-select__wrp >.reward-reply-select__tips")
                                .textContent()) === "添加新的回复";
                        const isCurrentReplay =
                            (await reward_setting_replay
                                .locator(".reward-reply-select__wrp >.reward-reply-select__display .wording")
                                .filter({ hasText: new RegExp(`^${[params.settingInfo.wxRewardText]}$`) })
                                .count()) === 1;

                        if (!isOneReplay && !isCurrentReplay) {
                            await reward_setting_replay
                                .locator(".reward-reply-select__wrp >.reward-reply-select__tips")
                                .click();
                            await reward_setting_replay
                                .locator(".reward-reply-select__display.can-select .wording")
                                .filter({ hasText: new RegExp(`^${[params.settingInfo.wxRewardText]}$`) })
                                .click();
                        }
                    } else {
                        (await reward_setting_bd
                            .locator(".reward-reply-setting .weui-desktop-switch__input")
                            .isChecked())
                            ? await reward_setting_bd
                                  .locator(".reward-reply-setting .weui-desktop-switch__box")
                                  .uncheck()
                            : 0;
                    }
                } else if (_type === "project") {
                }
            }

            const checkbox = reward_setting_ft.locator(".weui-desktop-icon-checkbox");
            if (!(await checkbox.isChecked())) await checkbox.check();
            await reward_setting_ft.locator(".weui-desktop-btn.weui-desktop-btn_primary").click();
        }
    }

    // 8. 互动设置
    if (params.settingInfo?.wxInteraction) {
        const openInteractionDialog = async () => {
            await page.locator(".appmsg-editor__setting-group.js_interaction_cell").click();
        };

        const setupPublicInteraction = async () => {
            const wxInteraction_bd = page.locator(".interaction-dialog .weui-desktop-dialog__bd");

            // 开启留言开关
            const commentSwitch = wxInteraction_bd.locator(
                ".comment-switcher.version5-same-set .weui-desktop-switch__box",
            );
            const isSwitchChecked = await wxInteraction_bd
                .locator(".comment-switcher.version5-same-set .weui-desktop-switch__input")
                .isChecked();
            if (!isSwitchChecked) {
                await commentSwitch.check();
            }

            // 谁可以留言（兼容传参文本与页面文本的差异）
            const commentTextMap: Record<string, string> = {
                所有用户: "所有人",
                已关注的用户: "已关注的人",
                已关注7天及以上的用户: "已关注7天及以上的人",
            };
            const commentOption = params.settingInfo.wxInteraction?.Option;
            const commentKey = commentOption?.Comment ?? "";
            const commentText = commentTextMap[commentKey] ?? commentKey;
            const comment_area = wxInteraction_bd.locator(".comment-options-same-set");
            await comment_area
                .locator(".comment-options__radio .weui-desktop-form__check-label")
                .filter({ hasText: commentText })
                .click();
            await page.waitForTimeout(500);

            // 自动精选留言
            if (
                params.settingInfo.wxInteraction &&
                params.settingInfo.wxInteraction.Option?.elect_comment !== undefined
            ) {
                const electSwitchInput = wxInteraction_bd.locator(
                    ".comment-options-inner .comment-switcher .weui-desktop-switch__input",
                );
                const electSwitchBox = wxInteraction_bd.locator(
                    ".comment-options-inner .comment-switcher .weui-desktop-switch__box",
                );
                const electCount = await electSwitchInput.count();
                if (electCount > 0) {
                    const isElectChecked = await electSwitchInput
                        .first()
                        .isChecked({ timeout: 3000 })
                        .catch(() => false);
                    if (params.settingInfo.wxInteraction.Option.elect_comment && !isElectChecked) {
                        await electSwitchBox.first().check();
                    } else if (!params.settingInfo.wxInteraction.Option.elect_comment && isElectChecked) {
                        await electSwitchBox.first().uncheck();
                    }
                }
            }
        };

        await openInteractionDialog();

        if (params.settingInfo.wxInteraction.Type === "public") {
            await setupPublicInteraction();

            const wxInteraction_ft = page.locator(".interaction-dialog .weui-desktop-dialog__ft");
            const confirmBtn = wxInteraction_ft.locator(".weui-desktop-btn.weui-desktop-btn_primary");
            const isDisabled = await confirmBtn.evaluate((el) => el.classList.contains("weui-desktop-btn_disabled"));

            if (isDisabled) {
                // 确定按钮不可用：关闭留言开关 -> 点确定关闭弹窗 -> 重新打开弹窗 -> 开启留言开关
                const wxInteraction_bd = page.locator(".interaction-dialog .weui-desktop-dialog__bd");
                const commentSwitch = wxInteraction_bd.locator(
                    ".comment-switcher.version5-same-set .weui-desktop-switch__box",
                );
                await commentSwitch.uncheck();
                await confirmBtn.click();
                await page.waitForTimeout(300);
                await openInteractionDialog();
                await setupPublicInteraction();
            }

            await page
                .locator(".interaction-dialog .weui-desktop-dialog__ft .weui-desktop-btn.weui-desktop-btn_primary")
                .click();
        }
    }

    // 9. 原文链接
    if (params.settingInfo.wxOriginalText) {
        await page.locator("#js_article_url_area .allow_click_opr").click();
        await page.waitForTimeout(200);
        const poperInstance = page.locator('.popover:not([style*="display: none"])');
        const poperInput = poperInstance.locator("input.weui-desktop-form__input.js_url");
        await poperInput.fill(params.settingInfo.wxOriginalText);
        await poperInstance.locator(".popover_bar .btn_primary").click();
    }

    // 10. 创作来源
    if (params.settingInfo.wxCreationSource && params.settingInfo.wxCreationSource.length > 0) {
        const source = params.settingInfo.wxCreationSource[0];
        await page.locator("#js_claim_source_area .allow_click_opr").click();
        await page.waitForTimeout(200);
        const poperInstance = page.locator('.claim-source-dialog:not([style*="display: none"])');
        await poperInstance.locator(`.weui-desktop-form__check-label:has(input[value="${source}"])`).click();
        const confirmBtn = poperInstance.locator(".weui-desktop-dialog__ft .weui-desktop-btn_primary");
        await confirmBtn.waitFor({ state: "visible" });
        await confirmBtn.click();
    }

    // 11. 关闭平台推荐
    if (params.settingInfo.wxNotAllowRecommend === true) {
        await page.locator("#js_not_recommend_area .allow_click_opr").click();
        await page.waitForTimeout(200);
        const poperInstance = page.locator('.popover:not([style*="display: none"])');
        await poperInstance.locator('.frm_radio_item label[for="not_recomment_0"]').click();
    }

    await page.waitForTimeout(1000);
    task._timerRecord.PrePublish = Date.now();

    // 12. 发布
    const articleId = await new Promise<string>((resolve) => {
        // 定义响应处理函数
        const handleResponse = async (response: Response) => {
            const url = response.url();
            const query = parseUrlQueryString(url);
            if (url.includes("/cgi-bin/operate_appmsg") && query?.t === "ajax-response") {
                const jsonResponse = await response.json();
                page.off("response", handleResponse);
                resolve(jsonResponse?.appMsgId);
            }
        };

        // 开始监听响应事件
        page.on("response", handleResponse);

        // 点击发表
        (async () => {
            await page.locator("#editor_pannel #bottom_main #js_button_area  #js_send button").click();
            await page.waitForTimeout(200);

            if (params.settingInfo.timer) {
                await page
                    .locator(".mass-send .mass-send__td-setting", { hasText: "定时发表" })
                    .locator(".weui-desktop-switch__box")
                    .check();
                const dateD = Number(TimeFormatter.format(params.settingInfo.timer, "d"));
                const nowD = Number(TimeFormatter.format(Date.now(), "d"));

                await page.locator(".mass-send__timer .weui-desktop-form__dropdown__dt").click();
                const dropMenu = page
                    .locator(".mass-send__timer .weui-desktop-dropdown-menu")
                    .locator(".weui-desktop-dropdown__list-ele");
                await dropMenu
                    .filter({
                        hasText:
                            dateD === nowD
                                ? "今天"
                                : dateD === nowD + 1
                                  ? "明天"
                                  : TimeFormatter.format(params.settingInfo.timer, "M月d日"),
                    })
                    .click();

                const timeDropMenu = page.locator(".mass-send__timer .weui-desktop-picker__time:visible");
                await timeDropMenu
                    .locator(".weui-desktop-form__input")
                    .fill(TimeFormatter.format(params.settingInfo.timer, "hh:mm"));
                await timeDropMenu.locator("i.weui-desktop-icon__time").click();
            }
        })();
    });
    await page.waitForTimeout(200);

    if (params.masssend) {
        const times = Number(
            await page
                .locator(".mass-send .multi-sent__notice_time")
                .locator('div[style="display: none;"] div:not([style*="display: none"]) span')
                .textContent(),
        );
        if (times === 0) return { code: 414, message: "今日群发次数用尽，请明天再试。", data: "" };
        await page.locator(".mass-send .mass_send__notify").locator(".weui-desktop-switch__box").check();
    } else {
        await page.locator(".mass-send .mass_send__notify").locator(".weui-desktop-switch__box").uncheck();
    }

    await page.waitForTimeout(200);

    // 再次点击发表
    await page.waitForSelector('.new_mass_send_dialog .weui-desktop-dialog__wrp:not([style*="display: none"])', {
        state: "visible",
    });
    await page
        .locator(".new_mass_send_dialog .weui-desktop-dialog .weui-desktop-dialog__ft button.weui-desktop-btn_primary")
        .click();

    // 点击继续发表
    await page.waitForSelector('.double_check_dialog .weui-desktop-dialog__wrp:not([style*="display: none"])', {
        state: "visible",
    });
    await page
        .locator(".double_check_dialog .weui-desktop-dialog .weui-desktop-dialog__ft button.weui-desktop-btn_primary")
        .click();

    await page.waitForTimeout(2000);

    // 原创校验弹窗
    const originalCheckDialog = page.locator(
        'div[data-transfer="true"][width\\.number="800"][height\\.number="800"] .weui-desktop-dialog__title',
    );
    if ((await originalCheckDialog.count()) > 0) {
        if ((await originalCheckDialog.textContent()) === "原创校验") {
            const popMassend = page.locator('div[data-transfer="true"][width\\.number="800"][height\\.number="800"]');
            await popMassend.locator(".weui-desktop-btn_primary", { hasText: "下一步" }).click();
            await popMassend.locator(".weui-desktop-btn_primary", { hasText: "继续" }).click();
        }
    }

    try {
        const startTime = Date.now();

        // biome-ignore lint/suspicious/noAsyncPromiseExecutor: <explanation>
        await new Promise<void>(async (resolve, reject) => {
            // 定义响应处理函数
            const handleResponse = async (response: Response) => {
                if (response.url().includes("/safe/safeuuid")) {
                    const jsonResponse = await response.json();
                    if (jsonResponse.errcode === 405) {
                        resolve();
                        page.off("response", handleResponse);
                    } else if (Date.now() - startTime > waitQrcodeResultMaxTime) {
                        reject();
                        page.off("response", handleResponse);
                    }
                }
            };

            // 开始监听响应事件
            page.on("response", handleResponse);

            // 获取微信验证二维码
            await page.waitForSelector(".dialog .js_wxchecks.safe_check img.qrcode.js_qrcode", {
                state: "visible",
            });
            const qrcodeSrc = await page
                .locator(".dialog .js_wxchecks.safe_check img.qrcode.js_qrcode")
                .first()
                .getAttribute("src");
            if (qrcodeSrc) {
                const imgUrl = new URL(qrcodeSrc, page.url()).href; // 处理相对路径
                const response = await axios({
                    method: "get",
                    url: imgUrl,
                    responseType: "arraybuffer", // 确保接收二进制数据
                });
                // 保存文件
                const qrcodeFilePath = path.join(tmpCachePath, "weixin_qrcode.jpg");
                fs.writeFileSync(qrcodeFilePath, response.data);
                params.safeQrcodeCallback?.(qrcodeFilePath);
            } else {
                throw new Error("qrcodeSrc is empty");
            }
        });
    } catch (error) {
        await page.close();
        return { code: 500, message: "群发二维码已过期，请重试发布。", data: "" };
    }

    await page.waitForTimeout(3000);
    await page.close();

    return success(articleId);
};
